import { Card, Box, Grid } from "@mui/material";
import StatsCounterCard from "components/Cards/StatsCounterCard";
import ConnectivityStatisticCard from "components/Cards/ConnectivityStatisticCard";
import LiveView from "components/LiveView";
import DeviceMap from "components/Maps/DeviceMap";
import LiveviewTimeline from "components/Timelines/LiveviewTimeline";
import PropTypes from "prop-types";

import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SoftBox from "components/SoftComponents/SoftBox";
import { Link } from "react-router-dom";
import SoftButton from "components/SoftComponents/SoftButton";
import { useState } from "react";
import { setDeviceColour } from "utils/device";
import { setDeviceDisplayType } from "utils/device";
import { getUserAccessLevel } from "utils/setup";
import { deviceViewWrapper } from "utils/wrapper";
import Divider from "@material-ui/core/Divider";
import SoftTypography from "components/SoftComponents/SoftTypography";
import HealthStatusCard from "components/Cards/HealthStatusCard";
import DeviceStatsCard from "components/Cards/DeviceStatsCard";

function getConnectivityIcon(device, user_type) {
    if (!device.active && getUserAccessLevel(user_type, 'technician')) {
        var text = `Inactive (System ${device.connected_state})`;
        return { text, component: 'visibility_off', colour: 'secondary' };
    }

    var text = `System ${device.connected_state}`;

    switch (device.connected_state) {
        case 'sleeping':
            if (getUserAccessLevel(user_type, 'technician')) {
                return { text, component: 'bedtime', colour: 'secondary' };
            }
            return { text: `System Online`, component: 'wifi', colour: 'success' };
        case 'offline':
            return { text, component: 'wifi_off', colour: 'error' };
        case 'reconnecting':
            return { text, component: 'wifi_protected_setup', colour: 'warning' };
        case 'online':
            return { text, component: 'wifi', colour: 'success' };
        default:
            return { text, component: 'question_mark', colour: 'dark' };
    }
}

function getDeviceTypeIcon(device, user_type) {
    if (!device.active && getUserAccessLevel(user_type, 'technician')) {
        var text = device.hardware_type == "bk-camera" ? `Camera Inactive` : `LiveView Inactive`
        return { text, component: 'visibility_off', colour: 'secondary' };
    }

    var text = device.hardware_type == "bk-camera" ? `Camera Active` : `LiveView Active`;
    switch (device.hardware_type) {
        case 'nuc':
        case 'visionect':
        case 'bk':
            return { text, component: 'photo', colour: 'dark' };
        case 'bk-camera':
            return { text, component: 'camera_alt', colour: 'dark' };
        default:
            return { text, component: 'question_mark', colour: 'dark' };
    }
}

function getConnectedStateColour(device, user_type) {
    if (!device.active && getUserAccessLevel(user_type, 'technician')) {
        return 'secondary';
    }

    switch (device.connected_state) {
        case 'sleeping':
            return 'secondary';
        case 'offline':
            return 'error';
        case 'reconnecting':
            return 'warning';
        case 'online':
            return 'success';
        default:
            return 'dark';
    }
}

function DeviceOverviewTab({ device, deviceLayout, user_type }) {

    const ClearwaysOverview = (
        <Grid container p={1} spacing={1}>
            <Grid item xxs={12} pb={3}>
                <Grid container spacing={1}>
                    <Grid item xxs={12}>
                        <ConnectivityStatisticCard title={{ text: device.active ? `Last Updated: ${device.last_updated}` : `Inactive: ${device.last_updated}`, fontWeight: 'bold' }} bgColor={device.active ? device.connected_state == 'online' ? 'success' : 'error' : 'secondary'} />
                    </Grid>
                    <Grid item xxs={12}>
                        <ConnectivityStatisticCard variant='h6' title={{ text: `eMinor Label: ${device.name}`, fontWeight: 'bold' }} />
                    </Grid>
                    <Grid item xxs={12} md={6}>
                        <ConnectivityStatisticCard variant='h6' title={{ text: `Serial No.: ${device.serial_number}`, fontWeight: 'bold' }} />
                    </Grid>
                    <Grid item xxs={12} md={6}>
                        <ConnectivityStatisticCard variant='h6' title={{ text: typeof device?.stop?.site_id == 'undefined' ? `Site ID: Not Set` : `Site ID: ${device?.stop?.site_id}`, fontWeight: 'bold' }} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xxs={12}>
                <Grid container spacing={1}>
                    <Grid item xxs={6}>
                        <StatsCounterCard
                            count={device.battery}
                            suffix="%"
                            title="Battery"
                            description=""
                        />
                    </Grid>
                    <Grid item xxs={6}>
                        <StatsCounterCard
                            count={device.rssi}
                            suffix="-dB"
                            title="Signal Strength"
                            description=""
                        />
                    </Grid>
                    <Grid item xxs={6}>
                        <StatsCounterCard
                            count={device.temperature}
                            suffix={<>&deg;C</>}
                            title="Temperature"
                            description=""
                        />
                    </Grid>
                    <Grid item xxs={6}>
                        <StatsCounterCard
                            count={device.humidity}
                            suffix="%"
                            title="Humidity"
                            description=""
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );

    const [header, subheader, triheader] = device.name.split("_").map((el) => el.trim());

    const clientOverview = (
        <SoftBox py={2} px={2}>
            <SoftBox mt={1} mx={1} display="flex" flexDirection="row" flexWrap="wrap" alignItems="baseline" sx={{ gap: 0.5 }}>
                {device.organisation_id == 'clearways' ? (
                    <>
                        <SoftTypography variant="h2" fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{`eMinor ${header}`}</SoftTypography>
                        <SoftTypography variant="subtitle1" fontWeight="bold" textTransform="capitalize" sx={{ color: "#777", minWidth: "fit-content" }}>{typeof device?.stop?.site_id == 'undefined' ? `Site ID: Not Set` : `Site ID: ${device?.stop?.site_id}`}</SoftTypography>
                    </>
                ) : (
                    <>
                        <SoftTypography variant="h2" fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                        <SoftTypography variant="subtitle1" fontWeight="bold" textTransform="capitalize" sx={{ color: "#777", minWidth: "fit-content" }}>{subheader}</SoftTypography>
                        <SoftTypography variant="subtitle2" fontWeight="bold" textTransform="capitalize" sx={{ color: "#999", verticalAlign: 'bottom', lineHeight: 2.1 }}>{triheader}</SoftTypography>
                    </>
                )}
            </SoftBox>
            <SoftBox my={2}>
                <Divider />
            </SoftBox>
            <Card>
                <SoftBox mx={2} my={2}>
                    <HealthStatusCard statistics={device} user_type={user_type} transparent={true} />
                    <SoftBox my={1}>
                        <Divider />
                    </SoftBox>
                    <SoftBox display="flex" flexDirection="row" justifyContent="space-evenly">
                        <StatsCounterCard
                            count={device.battery}
                            suffix="%"
                            title="Battery"
                            description=""
                            transparent={true}
                        />
                        <SoftBox m={1}>
                            <Divider orientation="vertical" />
                        </SoftBox>
                        <StatsCounterCard
                            count={device.temperature}
                            suffix={<>&deg;C</>}
                            title="Temperature"
                            description=""
                            transparent={true}
                        />
                    </SoftBox>
                    <SoftBox my={1}>
                        <Divider />
                    </SoftBox>
                    <DeviceStatsCard icon={getConnectivityIcon(device, user_type)} transparent={true} variant="h4" title={{ text: getConnectivityIcon(device, user_type).text, subtext: device.organisation_id == 'clearways' ? `Last Updated: ${device.last_updated}` : "", fontWeight: 'medium' }} />
                    <SoftBox my={1}>
                        <Divider />
                    </SoftBox>
                    <DeviceStatsCard icon={getDeviceTypeIcon(device, user_type)} transparent={true} variant="h4" title={{ text: getDeviceTypeIcon(device, user_type).text, fontWeight: 'medium' }} />
                </SoftBox>
            </Card>
        </SoftBox>
    );

    const overviewInfo = (
        <Card sx={{ height: "100%", overflowY: 'auto', background: '#f9f9f9', borderRadius: '12px' }} >
            {getUserAccessLevel(user_type, "technician") ? (
                <SoftBox py={2} px={2}>
                    <SoftBox mt={1} mx={1} display="flex" flexDirection="row" flexWrap="wrap" alignItems="baseline" sx={{ gap: 0.5 }}>
                        {device.organisation_id == 'clearways' ? (
                            <>
                                <SoftTypography variant="h2" fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{`eMinor ${header}`}</SoftTypography>
                                <SoftTypography variant="subtitle1" fontWeight="bold" textTransform="capitalize" sx={{ color: "#777", minWidth: "fit-content" }}>{typeof device?.stop?.site_id == 'undefined' ? `Site ID Not Set` : `(${device?.stop?.site_id})`}</SoftTypography>
                            </>
                        ) : (
                            <>
                                <SoftTypography variant="h2" fontWeight="bold" sx={{ color: "#333", mr: 1, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                                <SoftTypography variant="subtitle1" fontWeight="bold" textTransform="capitalize" sx={{ color: "#777", minWidth: "fit-content" }}>{subheader}</SoftTypography>
                                <SoftTypography variant="subtitle2" fontWeight="bold" textTransform="capitalize" sx={{ color: "#999", verticalAlign: 'bottom', lineHeight: 2.1 }}>{triheader}</SoftTypography>
                            </>
                        )}
                    </SoftBox>
                    <SoftBox my={2}>
                        <Divider />
                    </SoftBox>
                    <Card>
                        <SoftBox mx={2} my={2}>
                            <HealthStatusCard statistics={device} user_type={user_type} transparent={true} />
                            <SoftBox my={2}>
                                <Divider />
                            </SoftBox>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box display="flex" alignItems="center">
                                    <Box flex={1}>
                                        <StatsCounterCard
                                            count={device.battery}
                                            suffix="%"
                                            title="Battery"
                                            transparent={true}
                                        />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 2 }} />
                                    <Box flex={1}>
                                        <StatsCounterCard
                                            count={device.temperature}
                                            suffix={<>&deg;C</>}
                                            title="Temperature"
                                            transparent={true}
                                        />
                                    </Box>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" alignItems="center">
                                    <Box flex={1}>
                                        <StatsCounterCard
                                            count={device.rssi}
                                            suffix="-dB"
                                            title="Signal Strength"
                                            transparent={true}
                                        />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: "40px", mx: 2 }} />
                                    <Box flex={1}>
                                        <StatsCounterCard
                                            count={device.humidity}
                                            suffix="%"
                                            title="Humidity"
                                            transparent={true}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <SoftBox my={{ xxs: 1, xl: 2 }}>
                                <Divider />
                            </SoftBox>
                            <DeviceStatsCard icon={getConnectivityIcon(device, user_type)} transparent={true} variant={deviceLayout == 'mobile' ? "h6" : "h4"} title={{ text: getConnectivityIcon(device, user_type).text, subtext: device.organisation_id == 'clearways' ? `Last Updated: ${device.last_updated}` : "", fontWeight: 'medium' }} />
                            <SoftBox my={{ xxs: 1, xl: 2 }}>
                                <Divider />
                            </SoftBox>
                            <DeviceStatsCard icon={getDeviceTypeIcon(device, user_type)} transparent={true} variant={deviceLayout == 'mobile' ? "h6" : "h4"} title={{ text: getDeviceTypeIcon(device, user_type).text, fontWeight: 'medium' }} />
                            <SoftBox my={{ xxs: 1, xl: 2 }}>
                                <Divider />
                            </SoftBox>
                            <DeviceStatsCard icon={{ component: 'fingerprint', colour: 'dark' }} transparent={true} variant={deviceLayout == 'mobile' ? "h6" : "h4"} title={{ text: device.serial_number, fontWeight: 'medium' }} />
                            {/* <SoftBox my={{xxs: 1, xl: 2}}>
                                        <Divider />
                                    </SoftBox>
                                    <DeviceStatsCard transparent={true} variant={deviceLayout == 'mobile' ? "h6" : "h4"} title={{ text: `Display Type: ${setDeviceDisplayType(device)}`, fontWeight: 'medium' }} /> */}
                            {/* <SoftBox my={{xxs: 1, xl: 2}}>
                                        <Divider />
                                    </SoftBox>
                                    <DeviceStatsCard transparent={true} variant={deviceLayout == 'mobile' ? "h6" : "h4"} title={{ text: `Connectivity: ${device.gsm.network} ${device.gsm.operator} ${device.gsm.frequency}`, fontWeight: 'medium' }} /> */}
                        </SoftBox>
                    </Card>
                </SoftBox>
            ) : (
                clientOverview
            )}
        </Card>
    );

    return overviewInfo;
}

DeviceOverviewTab.propTypes = {
    device: PropTypes.object,
    deviceLayout: PropTypes.string,
    user_type: PropTypes.string
}

export default DeviceOverviewTab;